// The selected product's panel on Products (design/screens/products.html, "Selected: …"; UC-CATALOGUE-003 step 5): its catalogue
// facts and category details, the acting branch's cost, markup, price and stock (BR-CATALOGUE-026), its status and when it last
// changed, the serial a scan found (UC-CATALOGUE-004), its barcodes with Add, Generate and Remove (UC-CATALOGUE-007), a
// composite's components with the parts' total and what the package earns (UC-CATALOGUE-008), and the actions: Edit,
// Deactivate and Reactivate (UC-CATALOGUE-005), Delete product… with the Control PIN (UC-CATALOGUE-006). The band or markup behind
// the markup and whether the price is what it gives are pricing's answers (ProductPricing.tsx; UC-PRICING-001 step 4); its
// Branch prices, a marked-up product's Markup panel and the Price history are pricing's (ProductPrices.tsx; UC-PRICING-002,
// -003, -005). The cost's last receipt is inventory's screen, a later stage.
import { CATALOGUE_LIMITS, bp, cents, type BarcodeKind, type Cents, type ProductBarcodeView, type ProductLookupResult, type ProductView, type ProductWriteResult } from "@visionx/contracts";
import { exVat, sumCents, timesQuantity } from "@visionx/money";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products } from "../api/endpoints.catalogue";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatDate, formatDecimal, formatMoney, percentFromBp } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useOneStore } from "../session/one-store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { useStepUpWhileMounted } from "../stepup/StepUpProvider";
import { Alert, Chip, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Panel, StatusChip, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import {
  BARCODE_KIND_LABEL, FRAME_TYPE_SHORT, LENS_COLOUR_LABEL, LENS_DESIGN_LABEL, SERIAL_WORD, VAT_LABEL, categoryLabel, costWordOf, isLens, productReferenceRefusal,
  supplierPriceWords, useSupplierDiscount,
} from "./CatalogueShared";
import { ImportedFrom } from "./ImportsShared";
import { DocumentRef } from "./StockShared";
import { BranchPricesPanel, LaterPriceButton, MarkupPanel, PriceHistoryPanel, useBranchPricesShown } from "./ProductPrices";
import { markupKind, markupRow, priceWords, useLabCost, useProposal } from "./ProductPricing";
import type { ProductsState } from "./Products";
import { useLeaveGuard } from "./PatientsShared";

/** A serial a scan found, shown on its product's panel (UC-CATALOGUE-004 step 4). */
export interface ScannedSerial { productId: string; code: string; serial: NonNullable<ProductLookupResult["items"][number]["serial"]> }

/** BR-CATALOGUE-013: a scanned code's kind — 13 digits an EAN, 12 a UPC, anything else an alternate code (TC-CATALOGUE-114). */
export function barcodeKindOf(value: string): BarcodeKind {
  if (/^\d{13}$/.test(value)) return "SupplierEAN";
  if (/^\d{12}$/.test(value)) return "SupplierUPC";
  return "Alternate";
}

const BARCODE_WORDS: Readonly<Record<string, string>> = {
  check_digit: "The check digit is wrong — scan it again or check the number.",
  invalid_length: "That is not a whole EAN-13 or UPC-A code — scan it again.",
  reserved_prefix: "A 13-digit code beginning 20 is only ever the practice's own barcode — use Generate.",
  serial_shape: "That has the shape of a serial number, which only a unit carries.",
  too_short: `An alternate code has at least ${CATALOGUE_LIMITS.alternateBarcodeMin} characters.`,
  too_long: `An alternate code has at most ${CATALOGUE_LIMITS.alternateBarcodeMax} characters.`,
  invalid: "Printable characters without spaces only.",
  required: "Scan or type the barcode.",
  serialised_product: "A serialised product takes no barcode: each unit's barcode is its serial.",
};

/** A barcode refused, in words: a taken one names the product that holds it (UC-CATALOGUE-007 E2). */
export function BarcodeRefusal({ error }: { error: unknown }) {
  const f = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors[0] : undefined;
  const holderId = f?.code === "not_unique" && isApiError(error) && typeof error.meta.productId === "string" ? error.meta.productId : null;
  const holder = useQuery({ queryKey: ["product", holderId], queryFn: () => products.get(holderId!), enabled: holderId !== null, retry: false });
  if (!f) return <>{describeError(error)}</>;
  if (f.code === "not_unique") {
    if (isApiError(error) && typeof error.meta.serialId === "string") return <>Already a unit's serial — a barcode is never a serial number too.</>;
    const h = holder.data?.data;
    return <>Already on {h ? <Link to={`/products/${h.id}`}><span className="mono">{h.sku}</span> {h.displayName}</Link> : "another product"}.</>;
  }
  return <>{BARCODE_WORDS[f.code] ?? f.message}</>;
}

const dash = <span className="faint">—</span>;

function frameSummary(p: ProductView): ReactNode {
  const f = p.frame;
  if (!f) return null;
  const parts = [f.frameType ? FRAME_TYPE_SHORT[f.frameType] : null, f.sizeMarking, f.material].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}
function lensSummary(p: ProductView): ReactNode {
  const l = p.lens;
  if (!l) return null;
  const parts = [l.designType ? LENS_DESIGN_LABEL[l.designType] : null, l.refractiveIndex !== null ? `index ${formatDecimal(l.refractiveIndex, 3)}` : null, l.diameter !== null ? `${l.diameter} mm` : null, l.colour ? LENS_COLOUR_LABEL[l.colour] : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}
function contactLensSummary(p: ProductView): ReactNode {
  const c = p.contactLens;
  if (!c) return null;
  return [`${c.modality}, every ${c.replacementIntervalDays === 1 ? "day" : `${c.replacementIntervalDays} days`}`, `${c.lensesPerPack} lenses a pack`, c.baseCurve !== null ? `BC ${formatDecimal(c.baseCurve, 1)}` : null, c.diameter !== null ? `DIA ${formatDecimal(c.diameter, 1)}` : null].filter(Boolean).join(" · ");
}
function serviceSummary(p: ProductView): ReactNode {
  const s = p.service;
  if (!s) return null;
  const parts = [s.isConsultation ? "Consultation" : null, s.isFittingFee ? "Fitting fee" : null, s.durationMinutes !== null ? `${s.durationMinutes} minutes` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "No consultation or fitting fee";
}
/** A frame's model: its name and number, either alone (UC-CATALOGUE-003 step 5; TC-CATALOGUE-136). */
function frameModel(p: ProductView): ReactNode {
  const f = p.frame;
  if (!f || (!f.modelName && !f.modelNumber)) return null;
  if (!f.modelNumber) return f.modelName;
  const number = <span className="mono">{f.modelNumber}</span>;
  return f.modelName ? <>{f.modelName} · {number}</> : number;
}
/** A frame's colour: its name with its code — once where the code is the name — or either alone. */
function frameColour(p: ProductView): ReactNode {
  const f = p.frame;
  if (!f || (!f.colourName && !f.colourCode)) return null;
  const code = f.colourCode ? <>code <span className="mono">{f.colourCode}</span></> : null;
  if (!f.colourName) return code;
  if (!code || f.colourCode!.trim().toLowerCase() === f.colourName.trim().toLowerCase()) return f.colourName;
  return <>{f.colourName} · {code}</>;
}
function stockFlags(p: ProductView): string {
  if (p.category === "Service") return "Not a stock item";
  if (p.isComposite) return "Holds no stock of its own";
  if (!p.stocked) return "Made to order";
  return p.trackSerials ? "Kept in stock · serialised" : "Kept in stock";
}

/**
 * UC-CATALOGUE-003 step 5: the branch's last receipt beside the cost — "last received 14 Aug 2026 at R 490.00 on GRN-MEN-000019",
 * the document a link for a role that may open it (TC-CATALOGUE-139).
 */
function LastReceipt({ r }: { r: NonNullable<NonNullable<ProductView["atBranch"]>["lastReceipt"]> }) {
  return <> · last received {formatDate(r.on)}{r.unitCostCents !== null ? <> at <span className="mono">{formatMoney(r.unitCostCents)}</span></> : null}{r.document?.number ? <> on <DocumentRef doc={r.document} /></> : null}</>;
}

/** Where the list stood when a product was opened from it — its address and its page — so Edit can bring the user back to it. */
export interface ProductsListPlace { search: string; cursors: string[] }
/** What the panel's Edit hands the form: the list's place, for Cancel and Save to return to (TC-CATALOGUE-130). */
export interface ProductEditState { back?: ProductsListPlace | undefined }

export function ProductPanel({ id, scanned, onNotice, listPlace }: { id: string; scanned: ScannedSerial | null; onNotice: (n: { tone: "ok" | "warn" | "crit" | "info"; text: ReactNode } | null) => void; listPlace?: ProductsListPlace | undefined }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const block = useWriteBlock();
  const online = block === null;
  const title = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const canWrite = can(s, "catalogue.product.write");
  const canDelete = can(s, "catalogue.product.delete");
  const read = useQuery({ queryKey: ["product", id], queryFn: () => products.get(id) });
  const [dialog, setDialog] = useState<"deactivate" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(false);
  // A dialog open on the store's own price, or Price from a later day's, stays whatever the read after its refusal does to the
  // panel: each is kept while its dialog is open (UC-PRICING-002 A9 step 2; the closing check of the second fix round of 30 Oct
  // 2026, C2V2-2). The panel is keyed by its product (Products.tsx), so another product opened starts these afresh with the rest of
  // its state — its dialogs, what was typed in them, its price history (UC-CATALOGUE-003 A12; the verification of the third fix
  // round, 31 Oct 2026, C3V-ONE-1 and C3V-ONE-5: until then an effect let these go on a change of product, and nothing else).
  const [pricesHeld, setPricesHeld] = useState(false);
  const [laterHeld, setLaterHeld] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Scrolled into view once it is drawn — the first open of a product included, whose panel is drawn only after its read.
  const loaded = read.data !== undefined;
  useEffect(() => { if (loaded) panelRef.current?.scrollIntoView?.({ block: "nearest" }); }, [id, loaded]);
  const branch = s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? "this branch";
  const vatBp = s?.settings.vatRateBp ?? 1500;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["product", id] }); void qc.invalidateQueries({ queryKey: ["products"] }); };
  // Pricing's answers for the panel's Markup and Price (UC-PRICING-007 A1, UC-PRICING-011), asked once the product is read.
  const view = read.data?.data ?? null;
  const proposal = useProposal(view, view?.atBranch ?? null);
  const lab = useLabCost(view);
  // UC-CATALOGUE-003 A14 (change 9): the supplier's discount, read only for a product holding a supplier price.
  const supplierDiscount = useSupplierDiscount(view?.supplier?.id, view !== null && view.supplierPriceCents !== null);
  // A practice of one store shows Branch prices only while its one branch holds a price of its own; otherwise the product's price
  // is the store's, and Price history opens from this panel (UC-PRICING-002 A9; DVN's answers of 28 Oct 2026, change 13) — and
  // while one of the panel's dialogs is open (C2V2-2).
  const branchPrices = useBranchPricesShown(id) || pricesHeld;

  if (read.isPending) return <Loading what="the product" />;
  // A product not read yet shows its failure in the panel's place; one read before stays as it was last read, the failed read again said
  // above it, so a dialog open on it keeps what was typed and its refusal (UC-CATALOGUE-003 A13; the verification of the third fix round,
  // 31 Oct 2026, C3V-ONE-2: until then any failure took the panel's place, and the open dialog with it).
  const notFound = read.isError && isApiError(read.error) && read.error.code === "NOT_FOUND";
  if (read.data === undefined) {
    if (notFound) return <Alert tone="warn"><b>This product was not found.</b> It may have been deleted, or belong to another organisation.</Alert>;
    return <ErrorAlert error={read.error} />;
  }
  const p = read.data.data;
  const stale = !read.isError ? null : notFound
    ? <><b>{p.sku} was not found when read again.</b> It may have been deleted meanwhile. Shown as last read.</>
    : <><b>{p.sku} could not be read again.</b> {describeError(read.error)} Shown as last read.</>;
  const etag = read.data.etag;
  const f = p.atBranch;
  // The cost's word comes with its amount, in this one read (UC-CATALOGUE-003 A14 step 2; the review of change 9, 1 Nov 2026,
  // C9R-PWA-1, C9R-CONTRACT-1): never from pricing's cost lookup, which is asked after it, may fail, and is not read again on a save.
  const costWord = f ? costWordOf(p, f) : null;
  const price = f?.priceCents ?? p.priceCents;
  const vatText = p.vatTreatment === "Standard" ? `Standard ${percentFromBp(vatBp)}` : VAT_LABEL[p.vatTreatment];
  const details = frameSummary(p) ?? lensSummary(p) ?? contactLensSummary(p) ?? serviceSummary(p);
  // A lens whose price-list import has not made its list current is activated by that import, never by hand (UC-CATALOGUE-005
  // E3): the read says so, and the panel offers no Reactivate the server would refuse (TC-CATALOGUE-135).
  const importHolds = p.importedFrom !== null && !p.importedFrom.applied;
  // A lens its supplier's list hid from new orders comes back with a later list carrying its code, never by hand (UC-CATALOGUE-005
  // A5, E4; DVN's answer of 24 Oct 2026, the board's question 4): the read names the list, and the panel offers no Reactivate.
  const hiddenBy = p.hiddenByPriceList;
  const noReactivate = importHolds || hiddenBy !== null;
  const detailsLabel = p.frame ? "Frame" : p.lens ? "Lens" : p.contactLens ? "Contact lens" : p.service ? "Service" : null;

  // Said on the page, which keeps its notice above whichever product is opened next, so its refusal names the product too
  // (UC-CATALOGUE-003 A12 step 3, UC-CATALOGUE-005 A1 step 2; the verification of the fourth fix round, 1 Nov 2026, C4V-1).
  const reactivate = async () => {
    setBusy(true);
    try {
      await products.activate(p.id);
      onNotice({ tone: "ok", text: <><b>{p.sku} {p.displayName} reactivated.</b> Pickers offer it again.</> });
      refresh();
    } catch (err) { onNotice({ tone: "crit", text: <><b>{p.sku} {p.displayName} was not reactivated.</b> {describeError(err)}</> }); }
    finally { setBusy(false); }
  };

  return (
    <>
    {stale && <Alert tone="warn">{stale}</Alert>}
    <div className="grid-2 product-view" ref={panelRef}>
      <div className="panel" id="product-panel">
        <div className="panel-head">
          <h2>Selected: {p.displayName}</h2>
          <span className="right row c gap-8">
            {!branchPrices && <button className="btn sm" type="button" aria-expanded={history} onClick={() => setHistory((h) => !h)}>{history ? "Hide price history" : "Price history"}</button>}
            {/* UC-PRICING-002 A9 step 4: a price from a later day at a practice of one store (the verification of 30 Oct 2026, C13-1),
                its dialog kept while open though the store's own panel comes in (C2V2-2). */}
            {(!branchPrices || laterHeld) && <LaterPriceButton p={p} offered={!branchPrices} onHold={setLaterHeld} onDone={(words) => onNotice({ tone: "ok", text: words })} />}
            {canWrite && (online ? <Link className="btn sm" to={`/products/${p.id}/edit`} state={listPlace ? { back: listPlace } satisfies ProductEditState : undefined}>Edit</Link> : <button className="btn sm" type="button" disabled title={title}>Edit</button>)}
            {canWrite && (p.status === "Active"
              ? <button className="btn sm" type="button" disabled={!online || busy} title={title} onClick={() => setDialog("deactivate")}>Deactivate</button>
              : noReactivate ? null : <button className="btn sm" type="button" disabled={!online || busy} title={title} onClick={() => void reactivate()}>Reactivate</button>)}
            {canDelete && <button className="btn sm danger" type="button" disabled={!online} title={title} onClick={() => setDialog("delete")}>Delete product…<LockMark /></button>}
          </span>
        </div>
        <div className="panel-body">
          {scanned && (
            <Alert tone="info" role="status">
              {scanned.serial.detailsWithheld
                ? <><b>A unit with the code <span className="mono">{scanned.code}</span></b> is held at {scanned.serial.branchName}.</>
                : <><b>Serial <span className="mono">{scanned.serial.serialNumber}</span> · {scanned.serial.status ? SERIAL_WORD[scanned.serial.status] : "—"} · {scanned.serial.branchName}</b></>}
            </Alert>
          )}
          <KV rows={[
            ["SKU", <span className="mono">{p.sku}</span>],
            ["Category", <>{categoryLabel(p.category)}{p.subCategory ? ` · ${p.subCategory}` : ""}{p.isComposite ? " · composite" : ""}</>],
            ...(p.brand ? [["Brand", p.brand] as [ReactNode, ReactNode]] : []),
            ["Supplier", p.supplier ? <>{p.supplier.name || "A supplier"}{!p.supplier.active ? " · inactive" : ""}{p.supplierCode ? <> · code <span className="mono">{p.supplierCode}</span></> : null}</> : dash],
            ...(p.supplierPriceCents !== null ? [["Supplier price", supplierPriceWords(p.supplierPriceCents, supplierDiscount)] as [ReactNode, ReactNode]] : []),
            ["Cost", <>{f?.costCents != null ? <><span className="mono">{formatMoney(f.costCents)}</span>{costWord ? ` ${costWord}` : ""} · ex VAT</> : dash}{f?.lastReceipt ? <LastReceipt r={f.lastReceipt} /> : null}</>],
            markupRow(p, f, proposal.data?.data.proposal, lab.data?.data, branch),
            ["Price", priceWords(price, proposal.data?.data.proposal)],
            ["VAT", vatText],
            ...(p.nappiCode ? [["NAPPI", <span className="mono">{p.nappiCode}</span>] as [ReactNode, ReactNode]] : []),
            ...(p.tariffCode ? [["Tariff code", <><span className="mono">{p.tariffCode}</span>{p.tariffCodeInReference === false ? <span className="faint"> · not in the tariff list yet</span> : null}</>] as [ReactNode, ReactNode]] : []),
            ...(p.tag ? [["Tag", <span className="mono">{p.tag}</span>] as [ReactNode, ReactNode]] : []),
            ["Stock", stockFlags(p)],
            ...(detailsLabel && details ? [[detailsLabel, details] as [ReactNode, ReactNode]] : []),
            ...(frameModel(p) ? [["Model", frameModel(p)] as [ReactNode, ReactNode]] : []),
            ...(frameColour(p) ? [["Colour", frameColour(p)] as [ReactNode, ReactNode]] : []),
            ...(p.description ? [["Description", p.description] as [ReactNode, ReactNode]] : []),
            [p.status === "Active" ? "Active since" : "Inactive since", <div className="row c gap-8"><span>{formatDate(p.statusChangedAt ?? p.createdAt)}</span><StatusChip status={p.status} />{p.status !== "Active" && importHolds ? <span className="faint small">Activated by a price-list import</span> : null}{p.status !== "Active" && !importHolds && hiddenBy ? <span className="faint small">Hidden from new orders — not on {p.supplier?.name || "its supplier"}'s list of {formatDate(hiddenBy.listDate)}. A later list that carries {p.supplierCode ?? "its code"} brings it back.</span> : null}</div>],
            ...(p.notInLatestPriceList ? [["Price list", <Chip tone="warn">Not in latest price list{p.notInPriceListSince ? ` since ${formatDate(p.notInPriceListSince)}` : ""}</Chip>] as [ReactNode, ReactNode]] : []),
            ...(p.importedFrom ? [["Imported", <ImportedFrom importJobId={p.importedFrom.importJobId} rowNo={p.importedFrom.rowNo} prefix="from" />] as [ReactNode, ReactNode]] : []),
          ]} />
        </div>
      </div>
      <div className="stack" style={{ gap: 16 }}>
        {branchPrices && <BranchPricesPanel p={p} proposal={proposal.data?.data.proposal} historyOpen={history} onHistory={() => setHistory((h) => !h)} onNotice={(words) => onNotice({ tone: "ok", text: words })} onHold={setPricesHeld} />}
        {markupKind(p) === "markup" && <MarkupPanel p={p} />}
        {p.isComposite && <ComponentsPanel p={p} price={price} vatBp={vatBp} branch={branch} />}
        <BarcodesPanel p={p} canWrite={canWrite} online={online} title={title} onChanged={refresh} branch={branch} canReadStock={can(s, "inventory.stock.read")} />
      </div>
      {dialog === "deactivate" && (
        <DeactivateModal p={p} branch={branch} onClose={() => setDialog(null)} onDone={(r) => {
          setDialog(null);
          const composites = r.warnings.filter((w) => w.code === "ComponentOfActiveComposite");
          onNotice({ tone: composites.length > 0 ? "warn" : "ok", text: <>
            <b>{p.sku} {p.displayName} deactivated.</b> It leaves every picker; Reactivate offers it again.
            {composites.map((w) => <div key={w.message}>{w.message} — the kit cannot sell while a component is inactive.</div>)}
          </> });
          refresh();
        }} />
      )}
      {dialog === "delete" && (
        <DeleteProductModal p={p} etag={etag} onClose={() => setDialog(null)} onConflict={refresh} onDeactivate={() => setDialog("deactivate")} onDone={(r) => {
          setDialog(null);
          void qc.invalidateQueries({ queryKey: ["products"] });
          qc.removeQueries({ queryKey: ["product", p.id] });
          const state: ProductsState = { notice: `${r.sku} ${r.name} deleted.` };
          navigate(`/products${sp.toString() ? `?${sp.toString()}` : ""}`, { state });
        }} />
      )}
    </div>
    {history && <PriceHistoryPanel p={p} />}
    </>
  );
}

/** UC-CATALOGUE-008 step 7: the package's lines, the parts' total, and at the acting branch what its components cost and it earns. */
function ComponentsPanel({ p, price, vatBp, branch }: { p: ProductView; price: number | null; vatBp: number; branch: string }) {
  const reads = useQueries({ queries: p.components.map((c) => ({ queryKey: ["product", c.productId], queryFn: () => products.get(c.productId) })) });
  // Each quantity × amount is the money package's (TC-MONEY-020), the ex-VAT part of the price its `exVat` (TC-MONEY-001).
  const parts = p.components.every((c) => c.priceCents !== null) ? sumCents(p.components.map((c) => timesQuantity(cents(c.priceCents!), c.quantity))) : null;
  const costs = p.components.map((c, i) => { const cost = reads[i]?.data?.data.atBranch?.costCents; return cost == null ? null : timesQuantity(cents(cost), c.quantity); });
  const cost = costs.length > 0 && costs.every((x): x is Cents => x !== null) ? sumCents(costs as Cents[]) : null;
  const exPrice = price === null ? null : p.vatTreatment === "Standard" ? exVat(cents(price), bp(vatBp)) : price;
  return (
    <Panel title="Components" meta={`${p.components.length} line${p.components.length === 1 ? "" : "s"}`} tight foot={<span className="help">Holds no stock of its own: selling one takes its components from the branch (from Sprint 5).</span>}>
      <table>
        <thead><tr><th className="r">Qty</th><th>Product</th><th className="r">Price</th></tr></thead>
        <tbody>
          {p.components.map((c) => (
            <tr key={c.productId}>
              <td className="r num">{c.quantity} ×</td>
              <td><Link to={`/products/${c.productId}`}><span className="mono">{c.sku}</span></Link> {c.name}{c.status === "Inactive" && <> <Chip tone="neutral">Inactive</Chip></>}</td>
              <td className="money">{c.priceCents === null ? "—" : formatMoney(c.priceCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="panel-body">
        <KV rows={[
          ["Parts", parts === null ? <span className="faint">a component has no price</span> : <><span className="mono">{formatMoney(parts)}</span> incl. VAT</>],
          ["Package price", price === null ? <span className="faint">No price</span> : <><span className="mono">{formatMoney(price)}</span> incl. VAT</>],
          [`Components' cost at ${branch}`, cost === null ? <span className="faint">not known yet</span> : <><span className="mono">{formatMoney(cost)}</span> ex VAT</>],
          ["Earns", cost === null || exPrice === null ? <span className="faint">—</span> : <><span className="mono">{formatMoney(exPrice - cost)}</span> ex VAT</>],
        ]} />
      </div>
    </Panel>
  );
}

/**
 * UC-CATALOGUE-007: a product's barcodes; a scanner types the code and Enter. A serialised product holds none: each unit's serial
 * is its barcode, and the panel leads to the product's serials at the acting branch — Stock, searched for its SKU, its line open
 * (UC-INVENTORY-004 A8; TC-CATALOGUE-137).
 */
function BarcodesPanel({ p, canWrite, online, title, onChanged, branch, canReadStock }: { p: ProductView; canWrite: boolean; online: boolean; title: string | undefined; onChanged: () => void; branch: string; canReadStock: boolean }) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<ProductBarcodeView | null>(null);
  const addKey = useMemo(() => newIdempotencyKey(), [value.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  const [generateKey, setGenerateKey] = useState(() => newIdempotencyKey());
  const removeKey = useMemo(() => newIdempotencyKey(), [removing?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const writable = canWrite && !p.trackSerials;
  const add = async () => {
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true);
    setError(null);
    const added = () => { setValue(""); setAdding(false); onChanged(); };
    try {
      await products.addBarcode(p.id, { kind: barcodeKindOf(v), value: v }, addKey);
      added();
    } catch (err) {
      // Pressed again after a lost answer, under the same key, answered as carried out: linked once, its answer not kept — the
      // product, read again, lists it (UC-CATALOGUE-007 A7; BR-GOVERNANCE-016).
      if (carriedOut(err)) added(); else setError(err);
    }
    finally { setBusy(false); }
  };
  const generate = async () => {
    setBusy(true);
    setError(null);
    const generated = () => { setGenerateKey(newIdempotencyKey()); onChanged(); };
    try {
      await products.addBarcode(p.id, { kind: "Generated" }, generateKey);
      generated();
    } catch (err) { if (carriedOut(err)) generated(); else setError(err); }
    finally { setBusy(false); }
  };
  const remove = async (b: ProductBarcodeView) => {
    setBusy(true);
    setError(null);
    const removed = () => { setRemoving(null); onChanged(); };
    try {
      await products.removeBarcode(p.id, b.id, removeKey);
      removed();
    } catch (err) { if (carriedOut(err)) removed(); else setError(err); }
    finally { setBusy(false); }
  };
  const serialWord = p.category === "Frame" ? "Each frame's barcode is its serial." : "Each unit's barcode is its serial.";
  return (
    <Panel title="Barcodes" meta={p.trackSerials ? "serialised" : `${p.barcodes.length} · a scan of any finds it`}
      head={writable && <span className="right row c gap-8">
        <button className="btn sm" type="button" disabled={!online || busy} title={title} onClick={() => { setAdding(true); setError(null); }}><Icon name="plus" />Add barcode</button>
        <button className="btn sm" type="button" disabled={!online || busy} title={title} onClick={() => void generate()}>Generate</button>
      </span>} tight>
      {p.trackSerials ? <Empty title={serialWord} actions={p.heldByBranch && canReadStock ? <Link className="btn sm" to={`/stock?q=${encodeURIComponent(p.sku)}&product=${p.id}`}>Serials at {branch}</Link> : undefined}>
        Scan a unit's serial to find it; serialised products take no product barcode.
        {p.heldByBranch
          ? p.atBranch?.stock ? ` ${p.atBranch.stock.onHand} on hand at ${branch}.` : null
          : ` No serial yet: each ${p.category === "Frame" ? "frame" : "unit"} gets its own when it is received.`}
      </Empty> : <>
        {error !== null && !adding && <div className="panel-body"><Alert tone="crit"><b>Not saved.</b> <BarcodeRefusal error={error} /></Alert></div>}
        {adding && (
          <form className="panel-body" onSubmit={(e) => { e.preventDefault(); void add(); }}>
            <Field label="Barcode" htmlFor="barcode-new" error={undefined} help="Scan the box or type the code; 13 digits are an EAN, 12 a UPC, anything else an alternate code">
              <div className="row c">
                <TextInput id="barcode-new" mono autoFocus autoComplete="off" value={value} maxLength={64} onChange={(e) => { setValue(e.target.value); setError(null); }} error={error !== null} boxClass="grow" />
                <button className="btn primary sm" type="submit" disabled={busy || !value.trim()}>{busy ? "Adding…" : "Add"}</button>
                <button className="btn sm" type="button" onClick={() => { setAdding(false); setValue(""); setError(null); }}>Cancel</button>
              </div>
            </Field>
            {error !== null && <div className="error" role="alert"><BarcodeRefusal error={error} /></div>}
          </form>
        )}
        {p.barcodes.length === 0 ? (!adding && <Empty title="No barcode yet.">{writable ? "Add the box's barcode, or Generate the practice's own." : "A stock role adds or generates one."}</Empty>) : (
          <table>
            <thead><tr><th>Barcode</th><th>Kind</th><th>Prints as</th>{writable && <th />}</tr></thead>
            <tbody>
              {p.barcodes.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{b.value}</td>
                  <td>{BARCODE_KIND_LABEL[b.kind]}</td>
                  <td className="small">{b.symbology}</td>
                  {writable && (
                    <td className="r nw">
                      {removing?.id === b.id
                        ? <><span className="small">Remove {b.value}?</span> <button className="btn sm danger" type="button" disabled={busy || !online} onClick={() => void remove(b)}>Remove</button> <button className="btn sm" type="button" onClick={() => setRemoving(null)}>Keep</button></>
                        : <button className="btn sm" type="button" disabled={!online} aria-label={`Remove ${b.value}`} onClick={() => setRemoving(b)}>Remove</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>}
    </Panel>
  );
}

/** UC-CATALOGUE-005: what deactivating does, and the branch's stock that stays countable. */
function DeactivateModal({ p, branch, onClose, onDone }: { p: ProductView; branch: string; onClose: () => void; onDone: (r: ProductWriteResult) => void }) {
  const oneStore = useOneStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const onHand = p.atBranch?.stock?.onHand ?? 0;
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { onDone((await products.deactivate(p.id)).data); }
    catch (err) { setError(err); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Deactivate ${p.sku}`} meta={p.displayName} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy}>{busy ? "Deactivating…" : "Deactivate"}</button>
    </>}>
      {error !== null && <Alert tone="crit"><b>Not deactivated.</b> {describeError(error)}</Alert>}
      <Alert tone="info">It leaves every picker — new orders, composites, combination rules and lens options — but stays on every document and report, and it can be reactivated.</Alert>
      {/* A practice of one store has nowhere to transfer to (UC-INVENTORY-011 A12; the verification of 30 Oct 2026, C13-2). */}
      {onHand > 0 && <p>{onHand} on hand at {branch} can still be counted{oneStore ? " and adjusted" : ", adjusted and transferred"}.</p>}
    </Modal>
  );
}

/**
 * UC-CATALOGUE-006: the reason, then the Control PIN (`Delete`, record type `product`) under one Idempotency-Key per reason; a
 * product something refers to is refused before any PIN, naming what refers to it, with Deactivate offered instead. Gone under its
 * Control PIN dialog — another product opened, or the page left — it takes the PIN with it, nothing sent (UC-CATALOGUE-003 A12 step 2;
 * the closing check of the third fix round, 1 Nov 2026, C3V2-1).
 */
function DeleteProductModal({ p, etag, onClose, onDeactivate, onDone, onConflict }: { p: ProductView; etag: string | null; onClose: () => void; onDeactivate: () => void; onDone: (r: { sku: string; name: string }) => void; onConflict: () => void }) {
  const withStepUp = useStepUpWhileMounted();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState(false);
  useLeaveGuard(working);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setError(null); }, [reason]);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    if (!etag) { setError(new Error("The product was read without a version; reload and try again.")); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => { if (g.token) setWorking(true); return products.void(p.id, why, etag, { ...g, idempotencyKey: key }); }, {
        title: `Delete ${p.sku}`, reason: "none",
        description: `${p.sku} ${p.displayName} leaves every list, search and picker; its record is kept, and its SKU and barcodes are free for another product. The reason is written to the Control Log.`,
      });
      if (r) onDone(r.data);
    } catch (err) {
      // Pressed again after a lost answer — the same key, no second PIN — answered as carried out: deleted once, its answer not kept
      // (UC-CATALOGUE-006 A4; BR-GOVERNANCE-016).
      if (carriedOut(err)) { onDone({ sku: p.sku, name: p.name }); return; }
      setError(err);
      // Changed meanwhile: the product is read again at once, so the dialog's next try carries its new version.
      if (isApiError(err) && err.code === "VERSION_CONFLICT") onConflict();
    } finally { setBusy(false); setWorking(false); }
  };
  const refusal = productReferenceRefusal(error);
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  const stale = isApiError(error) && error.code === "VERSION_CONFLICT";
  return (
    <Modal title={`Delete ${p.sku}`} meta="Owner or Admin · recorded in error" onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>Cancel</button>
      {refusal !== null
        ? <button className="btn primary" type="button" onClick={onDeactivate}>Deactivate</button>
        : <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Deleting…" : "Delete product"}<LockMark /></button>}
    </>}>
      {working && <Alert tone="info" role="status"><b>Deleting {p.sku}…</b> This takes a few seconds. Keep this page open; Products shows the list when it is done.</Alert>}
      {refusal !== null
        ? <Alert tone="crit"><b>This product can't be deleted: it has {refusal}.</b> Deactivate it instead.</Alert>
        : error !== null && !reasonErr && <Alert tone="crit"><b>Not deleted.</b> {stale ? "The product was changed since you opened it; it has been read again — check it, and delete it again if it is still to go." : describeError(error)}</Alert>}
      <Alert tone="warn"><b>For a product that should never have existed</b> — added twice, or in the wrong category. It is voided, never removed: who, when and why are kept with it.</Alert>
      <Field label="Reason" required htmlFor="product-delete-reason" error={reasonErr} help="Written on the product and to the Control Log with the authoriser">
        <TextArea id="product-delete-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <div className="help"><LockMark>requires Control PIN</LockMark> — an Owner or Admin authorises the deletion.</div>
    </Modal>
  );
}
